import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Hotel, MapPin, Star, Waves, Wind, Search, ExternalLink, Percent, Heart, Map } from 'lucide-react';

// Booking.com Affiliate Configuration - CJ.com Tracking
const CJ_PUBLISHER_ID = '101689604';
const CJ_DEEPLINK_AD_ID = '15734849';  // Deep Link Ad ID
const CJ_BANNER_AD_ID = '17060544';    // Banner Ad ID

// Deep link base URL
const DEEPLINK_BASE = `https://www.tkqlhce.com/click-${CJ_PUBLISHER_ID}-${CJ_DEEPLINK_AD_ID}`;
// Banner URLs
const BANNER_CLICK = `https://www.tkqlhce.com/click-${CJ_PUBLISHER_ID}-${CJ_BANNER_AD_ID}`;
const BANNER_IMAGE = `https://www.tqlkg.com/image-${CJ_PUBLISHER_ID}-${CJ_BANNER_AD_ID}`;

// Build deep link with proper CJ format - destination URL parameter
const buildAffiliateLink = (bookingUrl) => {
  // Use German locale URLs (.de.html) for German display
  return `${DEEPLINK_BASE}?url=${encodeURIComponent(bookingUrl)}`;
};

// Regional Search Links - German locale URLs
const regionalLinks = {
  // Soma Bay region search
  somaBay: buildAffiliateLink('https://www.booking.com/searchresults.de.html?ss=Soma+Bay&dest_type=region&group_adults=2&no_rooms=1'),
  // Safaga city search  
  safaga: buildAffiliateLink('https://www.booking.com/searchresults.de.html?ss=Safaga&dest_type=city&group_adults=2&no_rooms=1'),
  // Hurghada city search
  hurghada: buildAffiliateLink('https://www.booking.com/searchresults.de.html?ss=Hurghada&dest_type=city&group_adults=2&no_rooms=1'),
  // All Red Sea hotels
  redSeaAll: buildAffiliateLink('https://www.booking.com/searchresults.de.html?ss=Rotes+Meer&dest_type=region&group_adults=2&no_rooms=1'),
};

const Hotels = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  // Affiliate Links for specific hotels - German language URLs
  const affiliateLinks = {
    palmRoyale: buildAffiliateLink('https://www.booking.com/hotel/eg/palm-royale-soma-bay.de.html'),
    movenpick: buildAffiliateLink('https://www.booking.com/hotel/eg/moevenpick-resort-soma-bay.de.html'),
    sheraton: buildAffiliateLink('https://www.booking.com/hotel/eg/sheraton-soma-bay-resort.de.html'),
    kempinski: buildAffiliateLink('https://www.booking.com/hotel/eg/kempinski-soma-bay.de.html'),
    breakers: buildAffiliateLink('https://www.booking.com/hotel/eg/breakers-diving-and-surfing-lodge.de.html'),
    caribbean: buildAffiliateLink('https://www.booking.com/hotel/eg/caribbean-world-soma-bay.de.html'),
  };

  const palmRoyale = {
    name: 'Palm Royale Resort Soma Bay',
    stars: 4,
    image: 'https://customer-assets.emergentagent.com/job_surfapp-revamp/artifacts/zbcoibzt_474912114.jpg',
    description: {
      de: 'Erschwingliches Resort direkt neben den besten Windsurf-, Kitesurf- und Wingfoiling-Spots. Natürlicher Schutz vor Strömungen und ganzjährig hervorragende Windbedingungen. Großes Spa und vielfältige Restaurantauswahl.',
      en: 'Affordable resort located right next to the best windsurfing, kitesurfing, and wingfoiling spots. Natural protection from currents and excellent wind conditions year-round. Large spa and diverse restaurant selection.'
    },
    amenities: {
      de: ['Direkt an Top-Spots', 'Windsurfing & Kitesurfing', 'Wing Foiling', 'Ganzjährig gute Windbedingungen', 'Großes Spa', 'Vielfältige Restaurants', 'Preis-Leistungs-Sieger'],
      en: ['Direct Access to Top Spots', 'Windsurfing & Kitesurfing', 'Wing Foiling', 'Year-round Wind Conditions', 'Large Spa', 'Diverse Restaurants', 'Best Value']
    },
    bookingUrl: affiliateLinks.palmRoyale,
    isSurfStation: true
  };

  const hotels = [
    {
      name: 'Mövenpick Resort Soma Bay',
      stars: 5,
      image: 'https://images.unsplash.com/photo-1599832976672-5923b10683d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiZWFjaCUyMHJlc29ydHxlbnwwfHx8Ymx1ZXwxNzYxMDM4MjEyfDA&ixlib=rb-4.1.0&q=85',
      description: {
        de: 'Familienfreundliches 5-Sterne Resort mit umfassendem Wassersportzentrum. Das Resort verfügt über 3 Pools (inklusive beheiztem Pool), erstklassiges Spa, 6 Restaurants und liegt in der Nähe eines Golfplatzes.',
        en: 'Family-friendly 5-star resort with comprehensive water sports center. The resort features 3 pools (including heated pool), first-class spa, 6 restaurants, and is near a golf course.'
      },
      amenities: {
        de: ['Kite-Surfing Center', 'Windsurfing', '3 Pools (beheizt)', 'Wellness & Spa', '6 Restaurants', 'Golf in der Nähe', 'Familienfreundlich'],
        en: ['Kite-Surfing Center', 'Windsurfing', '3 Pools (heated)', 'Wellness & Spa', '6 Restaurants', 'Golf nearby', 'Family-friendly']
      },
      bookingUrl: affiliateLinks.movenpick
    },
    {
      name: 'Sheraton Soma Bay Resort',
      stars: 5,
      image: 'https://images.unsplash.com/photo-1650806140318-b2bb4d358563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxSZWQlMjBTZWElMjBFZ3lwdHxlbnwwfHx8Ymx1ZXwxNzYxMDM4MjE5fDA&ixlib=rb-4.1.0&q=85',
      description: {
        de: 'Direkt am Nordstrand gelegen mit privatem Sandstrand und eigenem Kite-/Windsurf-Center. 310 Zimmer und Suiten, Shuttle-Service nach Hurghada, luxuriöse Strandsuiten verfügbar.',
        en: 'Located directly on the north shore with private sandy beach and own kite/windsurf center. 310 rooms and suites, shuttle service to Hurghada, luxurious beach suites available.'
      },
      amenities: {
        de: ['Privater Sandstrand', 'Kite-/Windsurf-Center', '310 Zimmer', 'Shuttle nach Hurghada', 'Strandsuiten', 'Wassersport vor Ort'],
        en: ['Private Sandy Beach', 'Kite/Windsurf Center', '310 Rooms', 'Shuttle to Hurghada', 'Beach Suites', 'On-site Watersports']
      },
      bookingUrl: affiliateLinks.sheraton
    },
    {
      name: 'Kempinski Hotel Soma Bay',
      stars: 5,
      image: 'https://images.unsplash.com/photo-1717916434101-7f1d246d2b28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBiZWFjaCUyMHJlc29ydHxlbnwwfHx8Ymx1ZXwxNzYxMDM4MjEyfDA&ixlib=rb-4.1.0&q=85',
      description: {
        de: 'Luxusresort mit 325 Zimmern, renommiert für exzellente Gastronomie und Spa-Einrichtungen. Bietet Windsurfing, Kitesurfing, Squash, Tennis und Schnorcheln. Perfekt für Familien und Paare mit atemberaubendem Sonnenuntergang am Strand.',
        en: 'Luxury resort with 325 rooms, renowned for excellent dining and spa facilities. Offers windsurfing, kitesurfing, squash, tennis, and snorkeling. Perfect for families and couples with stunning sunset beach views.'
      },
      amenities: {
        de: ['325 Luxuszimmer', '6 Restaurants', 'Premium Spa', 'Windsurfing & Kitesurfing', 'Tennis & Squash', 'Schnorcheln', 'Sonnenuntergangsstrand'],
        en: ['325 Luxury Rooms', '6 Restaurants', 'Premium Spa', 'Windsurfing & Kitesurfing', 'Tennis & Squash', 'Snorkeling', 'Sunset Beach']
      },
      bookingUrl: affiliateLinks.kempinski
    },
    {
      name: 'The Breakers Diving & Surfing Lodge',
      stars: 4,
      image: 'https://images.unsplash.com/photo-1574083085337-1aca04cd68e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBiZWFjaCUyMHJlc29ydHxlbnwwfHx8Ymx1ZXwxNzYxMDM4MjEyfDA&ixlib=rb-4.1.0&q=85',
      description: {
        de: 'Spezialisierte Lodge für Wassersportler. Perfekt für Taucher und Surfer, die eine authentische Erfahrung suchen. Direkt am Strand mit eigener Tauch- und Surfschule.',
        en: 'Specialized lodge for water sports enthusiasts. Perfect for divers and surfers seeking an authentic experience. Right on the beach with its own diving and surf school.'
      },
      amenities: {
        de: ['Tauchschule', 'Surfschule', 'Direkt am Strand', 'Authentisches Erlebnis', 'Wassersport-Fokus', 'Gemeinschafts-Atmosphäre'],
        en: ['Diving School', 'Surf School', 'Beachfront', 'Authentic Experience', 'Watersports Focus', 'Community Atmosphere']
      },
      bookingUrl: affiliateLinks.breakers
    }
  ];

  return (
    <div className="min-h-screen" data-testid="hotels-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-rose-600 to-pink-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Hotel className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('hotels.title')}</h1>
          <p className="text-xl text-rose-100">{t('hotels.subtitle')}</p>
        </div>
      </section>

      {/* Booking.com Banner */}
      <section className="py-8 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a 
            href={BANNER_CLICK} 
            target="_blank" 
            rel="noopener noreferrer"
            className="block mx-auto hover:opacity-90 transition-opacity"
            data-testid="booking-banner"
          >
            <img 
              src={BANNER_IMAGE} 
              width="660" 
              height="270" 
              alt="Booking.com - Hotels buchen" 
              className="mx-auto rounded-lg shadow-2xl max-w-full h-auto"
            />
          </a>
        </div>
      </section>

      {/* Regional Hotel Search */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Map className="w-4 h-4 mr-2" />
              {language === 'de' ? 'Alle Hotels in der Region' : 'All Hotels in the Region'}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {language === 'de' ? 'Hotels suchen & buchen' : 'Search & Book Hotels'}
            </h2>
            <p className="text-gray-600">
              {language === 'de' 
                ? 'Finden Sie das perfekte Hotel in Soma Bay, Safaga oder Hurghada' 
                : 'Find the perfect hotel in Soma Bay, Safaga or Hurghada'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Soma Bay */}
            <a
              href={regionalLinks.somaBay}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-6 text-white hover:shadow-2xl transition-all hover:-translate-y-1"
              data-testid="search-soma-bay"
            >
              <div className="flex items-center justify-between mb-4">
                <Hotel className="w-10 h-10" />
                <ExternalLink className="w-5 h-5 opacity-60 group-hover:opacity-100" />
              </div>
              <h3 className="text-xl font-bold mb-2">Soma Bay</h3>
              <p className="text-blue-100 text-sm">
                {language === 'de' 
                  ? 'Luxusresorts direkt am Roten Meer' 
                  : 'Luxury resorts on the Red Sea'}
              </p>
              <div className="mt-4 flex items-center text-sm font-semibold">
                <Search className="w-4 h-4 mr-2" />
                {language === 'de' ? 'Hotels anzeigen' : 'Show Hotels'}
              </div>
            </a>

            {/* Safaga */}
            <a
              href={regionalLinks.safaga}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl p-6 text-white hover:shadow-2xl transition-all hover:-translate-y-1"
              data-testid="search-safaga"
            >
              <div className="flex items-center justify-between mb-4">
                <Hotel className="w-10 h-10" />
                <ExternalLink className="w-5 h-5 opacity-60 group-hover:opacity-100" />
              </div>
              <h3 className="text-xl font-bold mb-2">Safaga</h3>
              <p className="text-teal-100 text-sm">
                {language === 'de' 
                  ? 'Windsurf-Paradies mit Charme' 
                  : 'Windsurfing paradise with charm'}
              </p>
              <div className="mt-4 flex items-center text-sm font-semibold">
                <Search className="w-4 h-4 mr-2" />
                {language === 'de' ? 'Hotels anzeigen' : 'Show Hotels'}
              </div>
            </a>

            {/* Hurghada */}
            <a
              href={regionalLinks.hurghada}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 text-white hover:shadow-2xl transition-all hover:-translate-y-1"
              data-testid="search-hurghada"
            >
              <div className="flex items-center justify-between mb-4">
                <Hotel className="w-10 h-10" />
                <ExternalLink className="w-5 h-5 opacity-60 group-hover:opacity-100" />
              </div>
              <h3 className="text-xl font-bold mb-2">Hurghada</h3>
              <p className="text-orange-100 text-sm">
                {language === 'de' 
                  ? 'Große Auswahl & Nachtleben' 
                  : 'Wide selection & nightlife'}
              </p>
              <div className="mt-4 flex items-center text-sm font-semibold">
                <Search className="w-4 h-4 mr-2" />
                {language === 'de' ? 'Hotels anzeigen' : 'Show Hotels'}
              </div>
            </a>
          </div>

          {/* All Red Sea Hotels Button */}
          <div className="text-center">
            <a
              href={regionalLinks.redSeaAll}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-gray-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
              data-testid="search-all-red-sea"
            >
              <Map className="w-5 h-5 mr-2" />
              {language === 'de' ? 'Alle Hotels am Roten Meer' : 'All Red Sea Hotels'}
              <ExternalLink className="w-5 h-5 ml-2" />
            </a>
          </div>
          
          <p className="text-center text-sm text-gray-500 mt-6">
            {language === 'de' 
              ? '✓ Kostenlose Stornierung bei den meisten Hotels • ✓ Keine Buchungsgebühren • ✓ Bestpreisgarantie' 
              : '✓ Free cancellation at most hotels • ✓ No booking fees • ✓ Best price guarantee'}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Erstklassige Unterkünfte in Soma Bay' : 'First-Class Accommodation in Soma Bay'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {language === 'de' 
                ? 'Soma Bay bietet exzellente Hotels direkt am Roten Meer - perfekt für Wassersport-Enthusiasten. Alle Hotels verfügen über erstklassige Windsurf- und Kitesurf-Einrichtungen und liegen in unmittelbarer Nähe zu unserem Surfmotion Center.'
                : 'Soma Bay offers excellent hotels directly on the Red Sea - perfect for water sports enthusiasts. All hotels feature first-class windsurfing and kitesurfing facilities and are in close proximity to our Surfmotion Center.'}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Hotel - Palm Royale */}
      <section className="py-16 bg-gradient-to-br from-rose-50 to-orange-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-rose-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              <Heart className="w-4 h-4 mr-2 fill-white" />
              {language === 'de' ? 'Unser Favorit - Surfmotion Station' : 'Our Favorite - Surfmotion Station'}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Unser Heimat-Hotel' : 'Our Home Hotel'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Die Surfmotion Station befindet sich direkt am Palm Royale Resort - Ihr perfekter Ausgangspunkt für unvergessliche Wassersport-Erlebnisse!'
                : 'The Surfmotion Station is located directly at Palm Royale Resort - your perfect starting point for unforgettable watersports experiences!'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-rose-500">
            <div className="relative h-96 overflow-hidden">
              <img 
                src={palmRoyale.image}
                alt={palmRoyale.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full shadow-lg flex items-center">
                {[...Array(palmRoyale.stars)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <div className="absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold">
                {language === 'de' ? 'Empfohlen' : 'Recommended'}
              </div>
            </div>

            <div className="p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{palmRoyale.name}</h3>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="w-5 h-5 mr-2" />
                    <span>Soma Bay, Red Sea, Egypt</span>
                  </div>
                </div>
              </div>

              <div className="bg-rose-50 border-l-4 border-rose-500 p-4 mb-6">
                <p className="text-rose-800 font-semibold flex items-center">
                  <Waves className="w-5 h-5 mr-2" />
                  {language === 'de' 
                    ? 'Hier findest du unsere Surfmotion Station mit allen Wassersport-Angeboten!'
                    : 'Here you will find our Surfmotion Station with all watersports offerings!'}
                </p>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                {language === 'de' ? palmRoyale.description.de : palmRoyale.description.en}
              </p>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                  <Waves className="w-6 h-6 mr-2 text-rose-600" />
                  {language === 'de' ? 'Highlights' : 'Highlights'}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(language === 'de' ? palmRoyale.amenities.de : palmRoyale.amenities.en).map((amenity, i) => (
                    <div key={i} className="flex items-center text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                      <div className="w-2 h-2 bg-rose-600 rounded-full mr-2 flex-shrink-0"></div>
                      <span className="text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href={palmRoyale.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg"
              >
                {language === 'de' ? 'Verfügbarkeit prüfen auf Booking.com' : 'Check Availability on Booking.com'}
                <ExternalLink className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Favorite Hotels */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-rose-100 text-rose-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Heart className="w-4 h-4 mr-2 fill-rose-600" />
              {language === 'de' ? 'Unsere Empfehlungen' : 'Our Recommendations'}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'de' ? 'Favorisierte Hotels in Soma Bay' : 'Favorite Hotels in Soma Bay'}
            </h2>
            <p className="text-gray-600">
              {language === 'de' 
                ? 'Von uns getestete und empfohlene Unterkünfte in der Nähe unserer Station' 
                : 'Accommodations tested and recommended by us near our station'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {hotels.map((hotel, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col" 
                data-testid={`hotel-card-${index}`}
              >
                {/* Hotel Image */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={hotel.image} 
                    alt={hotel.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow-md flex items-center">
                    {[...Array(hotel.stars)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>

                {/* Hotel Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{hotel.name}</h3>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span>Soma Bay, Red Sea, Egypt</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {language === 'de' ? hotel.description.de : hotel.description.en}
                  </p>

                  {/* Amenities */}
                  <div className="mb-6 flex-grow">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Waves className="w-5 h-5 mr-2 text-rose-600" />
                      {language === 'de' ? 'Ausstattung & Angebote' : 'Amenities & Facilities'}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {(language === 'de' ? hotel.amenities.de : hotel.amenities.en).map((amenity, i) => (
                        <div key={i} className="flex items-center text-sm text-gray-700">
                          <div className="w-2 h-2 bg-rose-600 rounded-full mr-2 flex-shrink-0"></div>
                          <span className="line-clamp-1">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Booking.com Link */}
                  <a
                    href={hotel.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors mt-auto"
                  >
                    {language === 'de' ? 'Auf Booking.com ansehen' : 'View on Booking.com'}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* More Hotels Button */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              {language === 'de' 
                ? 'Nicht das Richtige dabei? Durchsuchen Sie alle verfügbaren Hotels:' 
                : 'Not found the right one? Browse all available hotels:'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={regionalLinks.somaBay}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                <Hotel className="w-5 h-5 mr-2" />
                Soma Bay
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
              <a
                href={regionalLinks.safaga}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-teal-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors"
              >
                <Hotel className="w-5 h-5 mr-2" />
                Safaga
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
              <a
                href={regionalLinks.hurghada}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-700 transition-colors"
              >
                <Hotel className="w-5 h-5 mr-2" />
                Hurghada
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Wind className="w-16 h-16 mx-auto mb-6 text-rose-600" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'de' ? 'Perfekte Bedingungen das ganze Jahr' : 'Perfect Conditions Year-Round'}
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            {language === 'de'
              ? 'Soma Bay ist eines der besten Ziele am Roten Meer für ganzjähriges Windsurfen und Kitesurfen. Ideale Windbedingungen, warmes Wasser und wunderschöne Lagunen, geschützt vor starken Strömungen, machen es zum perfekten Ort für alle Levels.'
              : 'Soma Bay is one of the best Red Sea destinations for year-round windsurfing and kitesurfing. Ideal wind conditions, warm water, and beautiful lagoons protected from strong currents make it the perfect spot for all levels.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Wind className="w-10 h-10 mx-auto mb-3 text-blue-600" />
              <h3 className="font-bold text-gray-900 mb-2">
                {language === 'de' ? '15-20 Knoten' : '15-20 Knots'}
              </h3>
              <p className="text-sm text-gray-600">
                {language === 'de' ? 'Konstante Winde' : 'Consistent Winds'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Waves className="w-10 h-10 mx-auto mb-3 text-blue-600" />
              <h3 className="font-bold text-gray-900 mb-2">
                {language === 'de' ? '22-28°C' : '22-28°C'}
              </h3>
              <p className="text-sm text-gray-600">
                {language === 'de' ? 'Wassertemperatur' : 'Water Temperature'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Hotel className="w-10 h-10 mx-auto mb-3 text-blue-600" />
              <h3 className="font-bold text-gray-900 mb-2">
                {language === 'de' ? '5-Sterne' : '5-Star'}
              </h3>
              <p className="text-sm text-gray-600">
                {language === 'de' ? 'Luxus-Resorts' : 'Luxury Resorts'}
              </p>
            </div>
          </div>
          <a
            href="https://reservations.surfmotion.com/reservation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-rose-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-rose-700 transition-colors"
          >
            {language === 'de' ? 'Jetzt registrieren' : 'Register Now'}
          </a>
        </div>
      </section>

      {/* Travel Insurance Section */}
      <section className="py-12 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-4xl">🛡️</span>
            <h2 className="text-2xl font-bold text-gray-900 mt-4">
              {language === 'de' ? 'Hotel gebucht? Reiseversicherung nicht vergessen!' : 'Hotel booked? Don\'t forget travel insurance!'}
            </h2>
            <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
              {language === 'de' 
                ? 'Stell dir vor: Der Traumurlaub ist gebucht, die Vorfreude riesig – und dann wirst du krank, der Flug wird gestrichen, oder dein Gepäck landet in Timbuktu statt Hurghada. Ohne Versicherung bleibst du auf den Kosten sitzen. Mit einer guten Reiseversicherung? Alles halb so wild.' 
                : 'Imagine: Dream vacation booked, excitement through the roof – then you get sick, the flight gets cancelled, or your luggage ends up in Timbuktu instead of Hurghada. Without insurance, you\'re stuck with the costs. With good travel insurance? No big deal.'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Reiserücktritt */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-2">✈️</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                {language === 'de' ? 'Reiserücktrittsversicherung' : 'Trip Cancellation Insurance'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {language === 'de' 
                  ? 'Krankheit, Unfall, Jobverlust? Geld zurück statt Stornogebühren.' 
                  : 'Illness, accident, job loss? Money back instead of cancellation fees.'}
              </p>
              <a 
                rel="sponsored" 
                href="https://www.awin1.com/cread.php?s=3338732&v=11705&q=329249&r=2786628"
                target="_blank"
                className="inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-5 py-2 rounded-full font-semibold hover:from-green-600 hover:to-emerald-700 transition-all text-sm"
              >
                {language === 'de' ? 'Mehr erfahren →' : 'Learn more →'}
              </a>
            </div>

            {/* Reisejahresschutz */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-2">🌍</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">
                {language === 'de' ? 'Reise-Jahresschutz' : 'Annual Travel Protection'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {language === 'de' 
                  ? 'Mehrere Reisen im Jahr? Einmal abschließen, immer geschützt.' 
                  : 'Multiple trips a year? Sign up once, always protected.'}
              </p>
              <a 
                rel="sponsored" 
                href="https://www.awin1.com/cread.php?s=3338901&v=11705&q=329248&r=2786628"
                target="_blank"
                className="inline-block bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-5 py-2 rounded-full font-semibold hover:from-blue-600 hover:to-cyan-700 transition-all text-sm"
              >
                {language === 'de' ? 'Jahresschutz ansehen →' : 'View annual plan →'}
              </a>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            {language === 'de' 
              ? 'Empfohlen: HanseMerkur – einer der führenden Reiseversicherer Deutschlands seit über 50 Jahren.' 
              : 'Recommended: HanseMerkur – one of Germany\'s leading travel insurers for over 50 years.'}
          </p>
        </div>
      </section>

    </div>
  );
};

export default Hotels;
